import * as PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import {
    SelectMi,
    Option,
    BoutonRadio,
    Fieldset,
    Container,
    Row,
    Col,
} from '@design-system-etat/dsfr-react';
import InputFiles from 'components/Commun/Form/InputFiles';
import BoutonEtape from 'components/form/boutonsEtape/BoutonEtape';

const ErreurPossession = ({
    etapeSuivante,
    etapePrecedente,
    handleUpdateSignalerArmeData,
    signalerArmeData,
}) => {
    const hookForm = useForm({
        mode: 'onChange',
        defaultValues: {
            selectionMotif: signalerArmeData.motifErreurSelectionValue,
            motifErreurArme: signalerArmeData.typeErreurMotif,
            justificatifFichier: signalerArmeData.justificatifFichier,
        },
    });
    const { handleSubmit, register, watch, setValue, formState } = hookForm;
    const [afficherContenuMotif, setAfficherContenuMotif] = useState(
        signalerArmeData.typeErreurMotif,
    );

    const dataMotifs = [
        {
            key: '1',
            value: 'cedee',
            libelle: 'Cédée',
            selected: false,
            disabled: false,
            hidden: false,
        },
        {
            key: '2',
            value: 'doublon',
            libelle: 'En doublon',
            selected: false,
            disabled: false,
            hidden: false,
        },
        {
            key: '3',
            value: 'perdue',
            libelle: 'Perdue',
            selected: false,
            disabled: false,
            hidden: false,
        },
        {
            key: '4',
            value: 'volee',
            libelle: 'Volée',
            selected: false,
            disabled: false,
            hidden: false,
        },
    ];

    useEffect(() => {
        setValue('selectionMotif', signalerArmeData.motifErreurSelectionValue);
        setValue('motifErreurArme', signalerArmeData.typeErreurMotif);
        setValue('justificatifFichier', signalerArmeData.justificatifFichier);
    }, []);

    useEffect(() => {
        if (watch('motifErreurArme')) {
            setAfficherContenuMotif(watch('motifErreurArme'));
            handleUpdateSignalerArmeData({ typeErreurMotif: watch('motifErreurArme') });
        }
    }, [watch('motifErreurArme')]);

    useEffect(() => {
        if (watch('selectionMotif')) {
            handleUpdateSignalerArmeData({
                motifErreurSelectionValue: watch('selectionMotif'),
                motifErreurSelectionLibelle: dataMotifs.find(
                    (motif) => motif.value === watch('selectionMotif'),
                ).libelle,
            });
        }
    }, [watch('selectionMotif')]);

    const etapeSuivanteCliquable = useMemo(() => {
        switch (watch('motifErreurArme')) {
            case 'non-detention':
                return watch('selectionMotif');
            case 'jamais-detention':
            case 'arme-doublon':
                return true;
            default:
                return false;
        }
    }, [watch('motifErreurArme'), watch('selectionMotif')]);

    const handleOnChangeForm = () => {
        if (
            watch('motifErreurArme') === 'jamais-detention' ||
            watch('motifErreurArme') === 'arme-doublon'
        ) {
            setValue('selectionMotif', null);
            handleUpdateSignalerArmeData({ motifErreurSelection: null });
        }

        handleUpdateSignalerArmeData({ typeErreurMotif: watch('motifErreurArme') });
    };

    const onSubmit = async (dataForm) => {
        if (dataForm) {
            if (dataForm.justificatifFichier) {
                handleUpdateSignalerArmeData({
                    justificatifFichier: dataForm.justificatifFichier,
                });
            }

            etapeSuivante();
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} onChange={handleOnChangeForm}>
            <Container margeExterne={false}>
                <FormProvider {...hookForm}>
                    <Fieldset
                        id="selectionMotif"
                        legende="Veuillez sélectionner un motif :"
                        alignementHorizontal
                        legendeEnGras
                    >
                        <BoutonRadio
                            type="radio"
                            name="motifErreurArme"
                            label="Je ne détiens plus cette arme"
                            value="non-detention"
                            {...register('motifErreurArme')}
                        />
                        <BoutonRadio
                            type="radio"
                            name="motifErreurArme"
                            label="Je n'ai jamais détenu cette arme"
                            value="jamais-detention"
                            {...register('motifErreurArme')}
                        />
                        <BoutonRadio
                            type="radio"
                            name="motifErreurArme"
                            label="Cette arme est enregistrée en double dans mon râtelier"
                            value="arme-doublon"
                            {...register('motifErreurArme')}
                        />
                    </Fieldset>
                    {afficherContenuMotif === 'non-detention' ? (
                        <Row goutierre>
                            <Col col={12} lg={3}>
                                <SelectMi
                                    name="selectionMotif"
                                    label="Motif"
                                    placeholder="Sélectionnez une option"
                                    required
                                    {...register('selectionMotif')}
                                >
                                    {dataMotifs.map((option) => (
                                        <Option key={option.key} value={option.value}>
                                            {option.libelle}
                                        </Option>
                                    ))}
                                </SelectMi>
                            </Col>
                            <Col lg={9}>
                                <InputFiles
                                    label="Ajouter un justificatif"
                                    name="justificatifFichier"
                                    error={
                                        formState.errors
                                            ? formState.errors.justificatifFichier
                                            : null
                                    }
                                />
                                <p>Si nécessaire vous pouvez joindre un fichier</p>
                            </Col>
                        </Row>
                    ) : null}
                    {afficherContenuMotif === 'jamais-detention' ||
                    afficherContenuMotif === 'arme-doublon' ? (
                        <Row>
                            <Col lg={12}>
                                Veuillez vérifier qu&apos;il ne s&apos;agisse pas d&apos;une erreur
                                d&apos;informations sur une de vos armes détenues (erreur de numéro
                                de numéro RGA ou erreur de numéro de série). <br />
                                S&apos;il s&apos;agit d&apos;une erreur d&apos;informations sur une
                                de vos armes, revenez à l&apos;étape précédente et sélectionnez
                                sélectionnez &ldquo;Les informations de mon arme sont
                                incorrectes&ldquo; afin d&apos;accéder au module de correction.
                                <br />
                                S&apos;il ne s&apos;agit pas d&apos;une erreur d&apos;informations,
                                continuez la procédure en cliquant sur &ldquo;Étape suivante&ldquo;.
                            </Col>
                        </Row>
                    ) : null}
                    <BoutonEtape
                        nameBtnSuivant="etapeSuivante"
                        labelBtnSuivant="Étape suivante"
                        labelBtnPrecedent="Revenir à l'étape précédente"
                        actionPrecedente={etapePrecedente}
                        btnSuivantDisabled={!etapeSuivanteCliquable}
                    />
                </FormProvider>
            </Container>
        </form>
    );
};

ErreurPossession.propTypes = {
    handleUpdateSignalerArmeData: PropTypes.func.isRequired,
    etapeSuivante: PropTypes.func.isRequired,
    etapePrecedente: PropTypes.func.isRequired,
    signalerArmeData: PropTypes.shape({
        uuidArme: PropTypes.string.isRequired,
        typeErreur: PropTypes.string.isRequired,
        typeErreurMotif: PropTypes.string.isRequired,
        motifErreurSelectionValue: PropTypes.string,
        motifErreurSelectionLibelle: PropTypes.string,
        justificatifFichier: PropTypes.array,
    }).isRequired,
};

export default ErreurPossession;
